import * as z from 'zod';
import { memberRole } from '../organization-members';
import { readUserRefSchema } from '../users';

// Enums
/**
 * What keeps SCIM provisioning from being switched on: the directory can only create people on a
 * domain the workspace proved, through a connection that already signs them in.
 */
export const scimPrerequisiteEnum = z.enum([
  'no_verified_domain',
  'connection_not_enabled',
]);
export type ScimPrerequisite = z.infer<typeof scimPrerequisiteEnum>;

/** The API keeps two unrevoked tokens at most: the room a rotation needs, and no more (409, 5702). */
export const SCIM_TOKENS_MAX = 2;

// 3. Read schema - all properties nullable (API response)
// The workspace's SCIM settings: a singleton sub-resource of the organization, whose id it carries.
// Workspace admins only (403), and 404 while the workspace is not entitled to single sign-on.
export const readScimSettingsSchema = z.object({
  id: z.uuid().nullable(),
  enabled: z.boolean().nullable(),
  /** What the identity provider is configured with, next to a token. */
  baseUrl: z.string().nullable(),
  /** Empty when SCIM can be switched on. */
  unmetPrerequisites: z.array(scimPrerequisiteEnum).nullable(),
  lastRequestAt: z.iso.datetime().nullable(),
  /** Members the directory manages, whether active or deactivated by it. */
  managedUserCount: z.number().nullable(),
  /** Managed members the directory deactivated: suspended in the workspace. */
  deactivatedUserCount: z.number().nullable(),
  /** Groups the directory pushed, mapped to a role or not. */
  groupCount: z.number().nullable(),
});
export type ReadScimSettings = z.infer<typeof readScimSettingsSchema>;

// 1. Reference schema with label field (for dropdowns, selects, links)
// A token has no name: it is told apart by when it was created.
export const readScimTokenRefSchema = z.object({
  id: z.uuid(),
  createdAt: z.iso.datetime(),
});
export type ReadScimTokenRef = z.infer<typeof readScimTokenRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const scimTokenRefSchema = z.object({
  id: z.uuid(),
});
export type ScimTokenRef = z.infer<typeof scimTokenRefSchema>;

// 3. Read schema - all properties nullable (API response)
// The list never carries the token's value: it is stored hashed and only the creation answer has it.
export const readScimTokenSchema = z.object({
  id: z.uuid().nullable(),
  createdAt: z.iso.datetime().nullable(),
  createdBy: z.lazy(() => readUserRefSchema).nullable(),
  lastUsedAt: z.iso.datetime().nullable(),
  /** The address the identity provider last called from. */
  lastUsedIp: z.string().nullable(),
  /** Neither used nor created in the last 90 days: a candidate for revocation. */
  unused: z.boolean().nullable(),
});
export type ReadScimToken = z.infer<typeof readScimTokenSchema>;

// 4. Created envelope - the creation answer, the only place the token's value ever appears. Keep it
// in the state of the component that shows it, never in a query cache or a store.
export const createdScimTokenSchema = readScimTokenSchema.extend({
  token: z.string().nullable(),
});
export type CreatedScimToken = z.infer<typeof createdScimTokenSchema>;

// Group mappings
/**
 * A group the directory pushed and the role it maps to. The list carries every group, mapped or not
 * (`role` null): a plain array, no field selection and no pagination. Admins only.
 */
export const readScimGroupMappingSchema = z.object({
  groupId: z.uuid().nullable(),
  displayName: z.string().nullable(),
  externalId: z.string().nullable(),
  memberCount: z.number().nullable(),
  /** `null` while the group maps to nothing: it then has no effect in Tinct. */
  role: memberRole.nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadScimGroupMapping = z.infer<typeof readScimGroupMappingSchema>;

/** Maps a group to a role. Mapping to `admin` requires `confirmAdmin: true` (400, 5703). */
export const updateScimGroupMappingSchema = z.object({
  role: memberRole,
  confirmAdmin: z.boolean().optional(),
});
export type UpdateScimGroupMapping = z.infer<typeof updateScimGroupMappingSchema>;

// Legacy exports
export const scimSettingsSchema = readScimSettingsSchema;
export type ScimSettings = ReadScimSettings;
export const scimTokenSchema = readScimTokenSchema;
export type ScimToken = ReadScimToken;
